import React, { useState, useEffect } from 'react'
import { DeleteOutlined, MoneyCollectOutlined, CheckOutlined } from '@ant-design/icons';
import PageHeading from '../../components/PageHeading';
import { debounce } from 'lodash';
import {
  Button,
  message,
  Table,
  Select,
  DatePicker,
  Tag,
  Popconfirm,
  Input
} from 'antd';
import axiosInstance from '../../utils/axios';
import fetchMiniList from '../../utils/miniFetcher.js';
import { dateFormatmicroSecond } from '../../utils/dateFormats';

const { RangePicker } = DatePicker;

const EventBookings = () => {
  const [bookings, setBookings] = useState({
    results: []
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({
    id: null,
    event: null,
    status: null,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    fetchMasters();
  }, []);

  useEffect(() => {
    const delayFetch = debounce(() => {
      fetchData();
    }, 550); // 550ms debounce

    delayFetch();

    return () => delayFetch.cancel();

    // eslint-disable-next-line
  }, [page, filters]);

  const fetchMasters = async () => {
    try {
      // Try different endpoints for events
      let eventResponse = await fetchMiniList("news_and_events/events/");
      if (!eventResponse.status) {
        eventResponse = await fetchMiniList("news_and_events/events/select");
      }
      if (eventResponse.status) {
        // Handle both array and paginated response (with results property)
        const eventsData = eventResponse.data.results || eventResponse.data;
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      } else {
        message.error(eventResponse.data);
      }
    } catch (error) {
      console.error("Error in fetchMasters:", error);
      message.error("An unexpected error occurred while fetching data");
    }
  };

  const cancel = () => message.info('Cancelled');

  const confirm = async (id) => {
    try {
      // Try different delete endpoints
      try {
        await axiosInstance.delete(`news_and_events/cancel-event-booking/${id}/`);
      } catch (e) {
        await axiosInstance.delete(`booking/event-bookings/${id}/`);
      }
      message.success("Deleted successfully");
      fetchData();
    } catch (err) {
      message.error("Cannot delete");
    }
  };

  const confirmPayment = async (id) => {
    try {
      await axiosInstance.post('news_and_events/admin-confirm-event-payment/', { booking_id: id });
      message.success('Payment confirmed successfully');
      fetchData();
    } catch (err) {
      message.error('Failed to confirm payment');
    }
  };

  const columns = [
    {
      title: 'Event Name',
      key: 'event',
      render: (_, record) => record.event?.heading || record.event_title || '-',
    },
    {
      title: 'Venue',
      key: 'venue',
      render: (_, record) => record.event?.venue || '-',
    },
    {
      title: 'Date & Time',
      key: 'datetime',
      render: (_, record) => `${record.event?.start_date || '-'} - ${record.event?.end_date || '-'} - ${record.event?.time || '-'}`,
    },
    {
      title: 'Booked at',
      key: 'created_at',
      render: (_, record) => dateFormatmicroSecond(record.created_at),
    },
    {
      title: 'Booked By',
      key: 'booked_by',
      render: (_, record) =>
        record.booked_by?.email?.trim()
          ? record.booked_by?.email
          : (record.booked_by_email?.trim() ? record.booked_by_email : "Anonymous"),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => `${record.amount_to_pay}`,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (record.paid === true || record.status === "paid") {
          return <Tag color="green">Paid</Tag>;
        } else if (record.status === "pending") {
          return <Tag color="red">Pending</Tag>;
        } else {
          return <Tag color="orange">{record.status || "Unknown"}</Tag>;
        }
      }
    },
    {
      title: 'Number of Seats',
      key: 'seats',
      render: (_, record) => record.number_of_tickets || record.total_tickets || '-',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <>
          {record.status === 'pending' && !record.paid && (
            <Popconfirm
              title="Confirm Payment"
              description="This will mark the booking as paid and update the status."
              onConfirm={() => confirmPayment(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" icon={<CheckOutlined />} style={{ color: 'green' }}>
                Confirm Payment
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="Delete"
            description="Deleting booking will make seat slot available for this booking also remove from report calculation, are you sure want to delete this ?"
            onConfirm={() => confirm(record.id)}
            onCancel={cancel}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      )
    }
  ];


  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);

      if (filters.id) params.append("id", filters.id);
      if (filters.event) params.append("event__heading__icontains", filters.event);
      if (filters.status === 'paid') params.append("is_reserved", "false");
      if (filters.status === 'reserved') params.append("is_reserved", "true");
      if (filters.startDate) params.append("created_at__date__gte", filters.startDate);
      if (filters.endDate) params.append("created_at__date__lte", filters.endDate);

      const response = await axiosInstance.get(`news_and_events/event-bookings/?${params.toString()}`);
      setBookings(response.data);
    } catch (error) {
      message.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='dashboard'>
      <PageHeading name={`Event Bookings ${bookings.count ? `(Total: ${bookings.count})` : ''}`} icon={<MoneyCollectOutlined />} />
      <div className="filters">
        <Input
          placeholder="Booking ID"
          style={{ width: 150, margin: "3px" }}
          value={filters.id}
          onChange={(e) => setFilters(prev => ({ ...prev, id: e.target.value }))}
          allowClear
        />&nbsp;
        <Select
          showSearch
          placeholder="Events"
          style={{ width: 180, margin: "3px" }}
          value={filters.event}
          onChange={(value) => setFilters(prev => ({ ...prev, event: value }))}
          options={events.map(e => ({ label: e.heading || e.name, value: e.heading || e.name }))}
          allowClear
        />&nbsp;
        <Select
          placeholder="Status"
          style={{ width: 120, margin: "3px" }}
          value={filters.status}
          onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
          options={[
            { label: 'All', value: null },
            { label: 'Paid', value: 'paid' },
            // { label: 'Reserved', value: 'reserved' }
          ]}
          allowClear
        />&nbsp;
        <RangePicker
          style={{ margin: "3px" }}
          onChange={(dates) => {
            if (dates) {
              setFilters(prev => ({ ...prev, startDate: dates[0].format("YYYY-MM-DD"), endDate: dates[1].format("YYYY-MM-DD") }));
            } else {
              setFilters(prev => ({ ...prev, startDate: null, endDate: null }));
            }
          }}
        />&nbsp;
        <Button onClick={() => setFilters({ id: null, event: null, status: null, startDate: null, endDate: null })}>
          Clear
        </Button>
      </div>
      <Table
        dataSource={bookings.results}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: 20,
          total: bookings.count,
          onChange: (newPage, _) => {
            setPage(newPage);
          }
        }}
      />
    </div>
  )
}

export default EventBookings
